// eslint-disable-next-line import-x/order
import type { AppProps } from 'next/app';
/**
 * Next will combine the CSS of the imported files by import order. In order to load the most important
 * CSS definitions first, we move those CSS imports to the beginning of the file.
 */

import './document.scss';

import Head from 'next/head';
import { generateNextSeo } from 'next-seo/pages';
import type { ComponentType } from 'react';

import { ErrorGone } from '#components/Error/ErrorGone';
import { ErrorServerError } from '#components/Error/ErrorServerError';
import { AppShellLayout } from '#components/Shell/AppShellLayout';
import { NoShellLayout } from '#components/Shell/NoShellLayout';
import { useShellLayout } from '#components/Shell/use-shell-layout';
import type { AppPageProps } from '#cutils/page-context/server-side-props';

import { theSansC5s } from '../client/styles/font';

const LIVE_ENVIRONMENT = ['master', 'main'];

function MyApp({ Component, pageProps }: AppProps<AppPageProps & { error?: number } & { depublished?: true }>) {
	useShellLayout(pageProps.type);

	let Layout: ComponentType<any>;
	if (pageProps.type === 'no-shell') {
		Layout = NoShellLayout;
	} else {
		Layout = AppShellLayout;
	}

	const nofollow = !LIVE_ENVIRONMENT.includes(pageProps.environment.constants.BRANCH);

	const fontStyles = (
		// eslint-disable-next-line @eslint-react/jsx-shorthand-boolean
		<style jsx global>
			{`
				html {
					font-family: ${theSansC5s.style.fontFamily};
				}
			`}
		</style>
	);

	// if the request results in a non standard error code it will be set here as an additional property
	// therefore we need to display the error page
	if (pageProps.error) {
		return (
			<>
				{fontStyles}
				<Head>{generateNextSeo({ noindex: true })}</Head>

				<Layout pageProps={pageProps} nofollow={nofollow}>
					<ErrorServerError statusCode={pageProps.error} />
				</Layout>
			</>
		);
	} else if (pageProps.depublished) {
		return (
			<>
				{fontStyles}
				<Layout pageProps={pageProps} nofollow={nofollow}>
					<ErrorGone />
				</Layout>
			</>
		);
	} else {
		return (
			<>
				{fontStyles}
				<Layout pageProps={pageProps} nofollow={nofollow}>
					<Component {...pageProps} />
				</Layout>
			</>
		);
	}
}

export default MyApp;
